import { useState } from "react";
import { LMSLayout } from "@jambushrushti/components/lms/LMSLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@jambushrushti/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  BookDown,
  Search,
  AlertTriangle,
  CheckCircle,
  Clock,
} from "lucide-react";
import { Badge } from "@jambushrushti/components/ui/badge";
import { format } from "date-fns";

const issuedBooks: {
  id: number;
  memberId: string;
  memberName: string;
  bookNo: string;
  bookTitle: string;
  issueDate: string;
  dueDate: string;
  overdueDays: number;
  status: string;
}[] = [];

export default function BookReceive() {
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<any>(null);

  const handleReceive = (issue: any) => {
    setSelectedIssue(issue);
    setConfirmDialog(true);
  };

  const returnDate = new Date();

  return (
    <LMSLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Book Receive</h1>
          <p className="text-muted-foreground">
            Process book returns from members
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Quick Receive Form */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookDown className="h-5 w-5 text-primary" />
                Quick Return
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="memberId">Member ID *</Label>
                  <div className="relative">
                    <Input id="memberId" placeholder="e.g., M-001" />
                    <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bookNo">Book No *</Label>
                  <div className="relative">
                    <Input id="bookNo" placeholder="e.g., B-001" />
                    <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Return Date</Label>
                  <Input
                    value={format(returnDate, "dd/MM/yyyy")}
                    disabled
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="returnCondition">Return Condition *</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="damaged">Damaged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button className="w-full gap-2" variant="hero" size="lg">
                <BookDown className="h-5 w-5" />
                Process Return
              </Button>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-green-500">
                    Unavailable
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Returns Today
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-amber-500">
                    Unavailable
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Due This Week
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-destructive">
                    Unavailable
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Overdue Items
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Issued Books Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Currently Issued Books
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead>Member</TableHead>
                    <TableHead>Book</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issuedBooks.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={20}
                        className="py-8 text-center text-muted-foreground"
                      >
                        No records available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    issuedBooks.map(issue => (
                      <TableRow
                        key={issue.id}
                        className="hover:bg-secondary/30"
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">{issue.memberName}</p>
                            <p className="text-xs text-muted-foreground">
                              {issue.memberId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{issue.bookTitle}</p>
                            <p className="text-xs text-muted-foreground">
                              {issue.bookNo}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{issue.issueDate}</TableCell>
                        <TableCell>{issue.dueDate}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                issue.status === "Active"
                                  ? "default"
                                  : "destructive"
                              }
                            >
                              {issue.status}
                            </Badge>
                            {issue.overdueDays > 0 && (
                              <span className="flex items-center gap-1 text-xs text-destructive">
                                <AlertTriangle className="h-3 w-3" />
                                {issue.overdueDays} days
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReceive(issue)}
                          >
                            Receive
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Confirm Book Return
              </DialogTitle>
              <DialogDescription>
                Please verify the return details:
              </DialogDescription>
            </DialogHeader>
            {selectedIssue && (
              <div className="space-y-3 py-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Member:</span>
                  <span className="font-medium">
                    {selectedIssue.memberName} ({selectedIssue.memberId})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Book:</span>
                  <span className="font-medium">
                    {selectedIssue.bookTitle} ({selectedIssue.bookNo})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Issue Date:</span>
                  <span className="font-medium">{selectedIssue.issueDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Return Date:</span>
                  <span className="font-medium">
                    {format(returnDate, "dd/MM/yyyy")}
                  </span>
                </div>
                {selectedIssue.overdueDays > 0 && (
                  <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      <span className="font-medium text-destructive">
                        Overdue by {selectedIssue.overdueDays} days
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog(false)}>
                Cancel
              </Button>
              <Button variant="hero" onClick={() => setConfirmDialog(false)}>
                Confirm Return
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </LMSLayout>
  );
}
